import React, { useEffect, useState } from "react";
import { getDatabase, ref, onValue, remove } from "firebase/database";

const containerStyle = {
  width: "100%",
  margin: 0,
  padding: "1.5rem",
};

const searchInputStyle = {
  padding: "0.7em 1em",
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  width: "100%",
  maxWidth: "340px",
  marginBottom: "2rem",
  fontSize: "1em",
  background: "#f9fafb",
  outline: "none",
};

function flattenIssues(data) {
  const issues = [];
  if (!data) return issues;

  Object.entries(data).forEach(([transporterId, children]) => {
    if (children && typeof children === "object") {
      Object.entries(children).forEach(([childKey, childValue]) => {
        if (childValue && typeof childValue === "object") {
          if ("issue" in childValue) {
            const issueData = childValue;
            issues.push({
              id: childKey,
              transporterId,
              clientId: issueData.clientId || "",
              clientName: issueData.clientName || "",
              issue: issueData.issue || "",
              orderId: issueData.orderId || "",
              truckNumber: issueData.truckNumber || "",
              truckId: issueData.truckId || "",
              route: issueData.route || "",
              from: issueData.from || "",
              to: issueData.to || "",
              load: issueData.load || "",
              unit: issueData.unit || "",
              driverNumber: issueData.driverNumber || "",
              dispatchDate: issueData.dispatchDate || "",
              reportedAt: issueData.reportedAt || 0,
            });
          } else {
            Object.entries(childValue).forEach(([issueId, issueData]) => {
              if (issueData && typeof issueData === "object") {
                issues.push({
                  id: issueId,
                  transporterId,
                  clientId: issueData.clientId || childKey,
                  clientName: issueData.clientName || "",
                  issue: issueData.issue || "",
                  orderId: issueData.orderId || "",
                  truckNumber: issueData.truckNumber || "",
                  truckId: issueData.truckId || "",
                  route: issueData.route || "",
                  from: issueData.from || "",
                  to: issueData.to || "",
                  load: issueData.load || "",
                  unit: issueData.unit || "",
                  driverNumber: issueData.driverNumber || "",
                  dispatchDate: issueData.dispatchDate || "",
                  reportedAt: issueData.reportedAt || 0,
                });
              }
            });
          }
        }
      });
    }
  });

  return issues;
}

const Issues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [profiles, setProfiles] = useState({});
  const [selectedIssue, setSelectedIssue] = useState(null); // for modal
  const [selectingIssues, setSelectingIssues] = useState(false);
  const [selectedIssueIds, setSelectedIssueIds] = useState([]);
  const [showResolveConfirmation, setShowResolveConfirmation] = useState(false);

  useEffect(() => {
    const db = getDatabase();
    const issuesRef = ref(db, "transport_issues");

    const unsubscribeIssues = onValue(issuesRef, (snapshot) => {
      const data = snapshot.val();
      const flatIssues = flattenIssues(data);
      flatIssues.sort((a, b) => (b.reportedAt || 0) - (a.reportedAt || 0));
      setIssues(flatIssues);
      setLoading(false);
    });

    const profilesRef = ref(db, "profileDashboard");
    const transporterProfilesRef = ref(db, "transporter-profile");
    let unsubscribeProfiles = () => {};
    let unsubscribeTransporterProfiles = () => {};
    const lookup = {};

    unsubscribeProfiles = onValue(profilesRef, (snapshot) => {
      const data = snapshot.val();
      if (data && typeof data === "object") {
        Object.entries(data).forEach(([uid, profile]) => {
          const companyName =
            profile.company_name ||
            profile.companyName ||
            profile.transporterName ||
            profile.firmName ||
            profile.name ||
            "";
          lookup[uid] = {
            companyName,
            username: profile.username || profile.name || "",
          };
        });
      }
      setProfiles({ ...lookup });
    });

    unsubscribeTransporterProfiles = onValue(
      transporterProfilesRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data && typeof data === "object") {
          Object.entries(data).forEach(([uid, profile]) => {
            if (profile && typeof profile === "object") {
              const companyName = profile.company || "";
              if (companyName) {
                if (lookup[uid]) {
                  lookup[uid].companyName = companyName;
                } else {
                  lookup[uid] = { companyName, username: "" };
                }
              }
            }
          });
        }
        setProfiles({ ...lookup });
      },
    );

    return () => {
      unsubscribeIssues();
      unsubscribeProfiles();
      unsubscribeTransporterProfiles();
    };
  }, []);

  const toggleIssueSelection = (issueId) => {
    setSelectedIssueIds((prev) =>
      prev.includes(issueId)
        ? prev.filter((id) => id !== issueId)
        : [...prev, issueId],
    );
  };

  const clearSelectedIssues = async () => {
    try {
      const db = getDatabase();

      await Promise.all(
        selectedIssueIds.map((issueId) => {
          const issue = issues.find((item) => item.id === issueId);

          if (!issue) return Promise.resolve();

          return remove(
            ref(db, `transport_issues/${issue.transporterId}/${issueId}`),
          );
        }),
      );

      setSelectedIssueIds([]);
      setSelectingIssues(false);
      setShowResolveConfirmation(false);
    } catch (error) {
      console.error("Failed to clear selected issues:", error);
      alert("Failed to clear the selected issues. Please try again.");
    }
  };

  const filteredIssues = issues.filter((issue) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (issue.orderId && issue.orderId.toLowerCase().includes(q)) ||
      (issue.truckNumber && issue.truckNumber.toLowerCase().includes(q)) ||
      (issue.route && issue.route.toLowerCase().includes(q)) ||
      (issue.issue && issue.issue.toLowerCase().includes(q)) ||
      (issue.transporterId && issue.transporterId.toLowerCase().includes(q)) ||
      (issue.clientId && issue.clientId.toLowerCase().includes(q)) ||
      (issue.load && String(issue.load).toLowerCase().includes(q)) ||
      (issue.driverNumber &&
        String(issue.driverNumber).toLowerCase().includes(q)) ||
      (issue.clientName && issue.clientName.toLowerCase().includes(q))
    );
  });

  // Styles
  const tableWrapperStyle = {
    overflowX: "auto",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,.08)",
    marginTop: "1.5rem",
    width: "100%",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "auto",
  };

  const thStyle = {
    position: "sticky",
    top: 0,
    background: "#f4f6f8",
    color: "#222",
    fontWeight: 700,
    padding: "12px",
    textAlign: "left",
    borderBottom: "2px solid #e5e7eb",
    zIndex: 1,
    fontSize: "1em",
  };

  const tdStyle = {
    padding: "12px",
    borderBottom: "1px solid #f0f0f0",
    fontSize: "0.98em",
    verticalAlign: "top",
    background: "#fff",
    maxWidth: 240,
    wordBreak: "break-word",
  };

  const rowEvenStyle = { background: "#fafbfc" };
  const rowHoverStyle = { background: "#f1f7fa" };

  const detailsBtnStyle = {
    padding: "6px 14px",
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: "0.9em",
    fontWeight: 600,
  };

  // Modal styles
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const modalStyle = {
    background: "#fff",
    borderRadius: 12,
    padding: "1.75rem",
    width: "90%",
    maxWidth: 520,
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
  };

  const modalRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid #f0f0f0",
    fontSize: "0.95em",
  };

  const modalLabelStyle = {
    color: "#666",
    fontWeight: 500,
    minWidth: 120,
  };

  const modalValueStyle = {
    color: "#111",
    fontWeight: 600,
    textAlign: "right",
    wordBreak: "break-word",
  };

  function renderProfileCell(uid) {
    const profile = profiles[uid];
    if (profile) {
      const companyName =
        profile.companyName && profile.companyName.trim() !== ""
          ? profile.companyName
          : null;
      const username =
        profile.username && profile.username.trim() !== ""
          ? profile.username
          : null;
      if (companyName) {
        if (username && username !== companyName) {
          return (
            <span>
              <span style={{ fontWeight: 600 }}>{companyName}</span>
              <br />
              <span style={{ color: "#888", fontSize: "0.97em" }}>
                {username}
              </span>
            </span>
          );
        }
        return <span style={{ fontWeight: 600 }}>{companyName}</span>;
      }
      if (username) return <span>{username}</span>;
      return <span>{uid}</span>;
    }
    return <span style={{ color: "#bbb" }}>{uid}</span>;
  }

  function formatDate(value) {
    if (!value) return <span style={{ color: "#bbb" }}>N/A</span>;
    const date = typeof value === "number" ? new Date(value) : new Date(value);
    if (isNaN(date.getTime())) return <span>{String(value)}</span>;
    return (
      <>
        <div>{date.toLocaleDateString()}</div>
        <div style={{ color: "#888", fontSize: "0.85em" }}>
          {date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
        </div>
      </>
    );
  }

  function DetailRow({ label, value }) {
    return (
      <div style={modalRowStyle}>
        <span style={modalLabelStyle}>{label}</span>
        <span style={modalValueStyle}>
          {value || <span style={{ color: "#bbb", fontWeight: 400 }}>N/A</span>}
        </span>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h1 style={{ fontSize: "2rem", fontWeight: 700, marginBottom: "1.5rem" }}>
        Transport Issues
      </h1>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "1.25rem",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          style={{
            padding: "8px 16px",
            background: selectingIssues ? "#6b7280" : "#d32f2f",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: "0.9em",
            fontWeight: 600,
          }}
          onClick={() => {
            setSelectingIssues((prev) => !prev);
            setSelectedIssueIds([]);
          }}
        >
          {selectingIssues ? "Cancel Clear Issues" : "Clear Issue"}
        </button>

        {selectingIssues && (
          <>
            <span style={{ color: "#555", fontSize: "0.9em" }}>
              {selectedIssueIds.length} issue
              {selectedIssueIds.length === 1 ? "" : "s"} selected
            </span>

            <button
              type="button"
              disabled={selectedIssueIds.length === 0}
              style={{
                padding: "8px 16px",
                background:
                  selectedIssueIds.length === 0 ? "#d1d5db" : "#0074cc",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor:
                  selectedIssueIds.length === 0 ? "not-allowed" : "pointer",
                fontSize: "0.9em",
                fontWeight: 600,
              }}
              onClick={() => setShowResolveConfirmation(true)}
            >
              Clear Selected
            </button>
          </>
        )}
      </div>
      <input
        style={searchInputStyle}
        type="text"
        placeholder="Search by Order ID, Truck, Load, Driver, Route, Issue..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <div style={{ marginTop: "2rem", textAlign: "center" }}>Loading...</div>
      ) : filteredIssues.length === 0 ? (
        <div style={{ marginTop: "2rem", textAlign: "center", color: "#888" }}>
          No transport issues found.
        </div>
      ) : (
        <div style={tableWrapperStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {selectingIssues && (
                  <th style={{ ...thStyle, width: 50 }}>
                    <input
                      type="checkbox"
                      checked={
                        filteredIssues.length > 0 &&
                        filteredIssues.every((issue) =>
                          selectedIssueIds.includes(issue.id),
                        )
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIssueIds((prev) => [
                            ...prev,
                            ...filteredIssues
                              .map((issue) => issue.id)
                              .filter((id) => !prev.includes(id)),
                          ]);
                        } else {
                          const filteredIds = new Set(
                            filteredIssues.map((issue) => issue.id),
                          );

                          setSelectedIssueIds((prev) =>
                            prev.filter((id) => !filteredIds.has(id)),
                          );
                        }
                      }}
                    />
                  </th>
                )}

                <th style={thStyle}>Order ID</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Issue</th>
                <th style={thStyle}>Transporter</th>
                <th style={thStyle}>Reported At</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              {filteredIssues.map((issue, idx) => {
                const isEven = idx % 2 === 1;
                return (
                  <tr
                    key={issue.id}
                    style={isEven ? rowEvenStyle : undefined}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.background =
                        rowHoverStyle.background)
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.background = isEven
                        ? rowEvenStyle.background
                        : "#fff")
                    }
                  >
                    {selectingIssues && (
                      <td
                        style={{
                          ...tdStyle,
                          width: 50,
                          textAlign: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIssueIds.includes(issue.id)}
                          onChange={() => toggleIssueSelection(issue.id)}
                        />
                      </td>
                    )}
                    <td style={tdStyle}>
                      {issue.orderId || (
                        <span style={{ color: "#bbb" }}>N/A</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {issue.clientName ? (
                        <span style={{ fontWeight: 600 }}>
                          {issue.clientName}
                        </span>
                      ) : (
                        renderProfileCell(issue.clientId)
                      )}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        color: "#d32f2f",
                        fontWeight: 700,
                      }}
                    >
                      {issue.issue || (
                        <span style={{ color: "#888", fontWeight: 400 }}>
                          No description
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {renderProfileCell(issue.transporterId)}
                    </td>
                    <td style={tdStyle}>{formatDate(issue.reportedAt)}</td>
                    <td style={tdStyle}>
                      <button
                        style={detailsBtnStyle}
                        onClick={() => setSelectedIssue(issue)}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showResolveConfirmation && (
        <div
          style={overlayStyle}
          onClick={() => setShowResolveConfirmation(false)}
        >
          <div
            style={{ ...modalStyle, maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                margin: "0 0 1rem",
                fontSize: "1.2rem",
                fontWeight: 700,
              }}
            >
              Confirm Clear Issues
            </h2>

            <p
              style={{
                margin: "0 0 1.5rem",
                color: "#444",
                lineHeight: 1.5,
              }}
            >
              Are these issues resolved?
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                type="button"
                onClick={() => setShowResolveConfirmation(false)}
                style={{
                  padding: "8px 18px",
                  background: "#e5e7eb",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={clearSelectedIssues}
                style={{
                  padding: "8px 18px",
                  background: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Yes, Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ========== DETAILS MODAL ========== */}
      {selectedIssue && (
        <div style={overlayStyle} onClick={() => setSelectedIssue(null)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1.25rem",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700 }}>
                Issue Details
              </h2>
              <button
                onClick={() => setSelectedIssue(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "1.4rem",
                  cursor: "pointer",
                  color: "#666",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <DetailRow label="Order ID" value={selectedIssue.orderId} />
            <DetailRow label="Issue" value={selectedIssue.issue} />
            <DetailRow
              label="Client"
              value={
                selectedIssue.clientName ||
                profiles[selectedIssue.clientId]?.companyName ||
                selectedIssue.clientId
              }
            />
            <DetailRow
              label="Transporter"
              value={
                profiles[selectedIssue.transporterId]?.companyName ||
                selectedIssue.transporterId
              }
            />
            <DetailRow label="Truck Number" value={selectedIssue.truckNumber} />
            <DetailRow
              label="Load"
              value={
                selectedIssue.load
                  ? `${selectedIssue.load}${selectedIssue.unit ? ` ${selectedIssue.unit}` : ""}`
                  : null
              }
            />
            <DetailRow
              label="Driver Number"
              value={selectedIssue.driverNumber}
            />
            <DetailRow
              label="Dispatch Date"
              value={
                selectedIssue.dispatchDate
                  ? typeof selectedIssue.dispatchDate === "number"
                    ? new Date(selectedIssue.dispatchDate).toLocaleString()
                    : String(selectedIssue.dispatchDate)
                  : null
              }
            />
            <DetailRow label="Route" value={selectedIssue.route} />
            <DetailRow label="From" value={selectedIssue.from} />
            <DetailRow label="To" value={selectedIssue.to} />
            <DetailRow
              label="Reported At"
              value={
                selectedIssue.reportedAt
                  ? new Date(selectedIssue.reportedAt).toLocaleString()
                  : null
              }
            />

            <div style={{ marginTop: "1.5rem", textAlign: "right" }}>
              <button
                onClick={() => setSelectedIssue(null)}
                style={{
                  padding: "8px 20px",
                  background: "#e5e7eb",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Issues;
